"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Box, CircularProgress, Chip, TextField, InputAdornment, Paper, Typography } from "@mui/material"
import { Add, People, Search } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import DataTable from "../../components/common/DataTable"
import EmptyState from "../../components/common/EmptyState"
import OrganizationFilter from "../../components/users/OrganizationFilter"
import ConfirmDialog from "../../components/common/ConfirmDialog"
import UserRowActions from "../../components/users/UserRowActions"
import useSessionState from "../../hooks/useSessionState"
import { getUsers, searchUsers, updateUser, deleteUser } from "../../services/user.service"

const Users = () => {
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user, selectedOrganization } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [searching, setSearching] = useState(false)
  const [selectedOrganizations, setSelectedOrganizations] = useSessionState(
    `users.organizationFilter.${user?._id}`,
    selectedOrganization ? [selectedOrganization] : [],
  )
  const sidebarOrganizationId = useRef(selectedOrganization?._id)

  // Picking a different organization in the sidebar resets the filter to it; a filter chosen here is kept otherwise
  useEffect(() => {
    if (selectedOrganization && selectedOrganization._id !== sidebarOrganizationId.current) {
      setSelectedOrganizations([selectedOrganization])
    }
    sidebarOrganizationId.current = selectedOrganization?._id
  }, [selectedOrganization])
  const [pendingAction, setPendingAction] = useState(null)
  const lastTargetName = useRef("")
  if (pendingAction) {
    lastTargetName.current = `${pendingAction.row.firstName} ${pendingAction.row.lastName}`
  }

  const isSuperAdmin = user?.role === "superadmin"
  const canManageUsers = ["admin", "superadmin"].includes(user?.role)

  useEffect(() => {
    if (isSuperAdmin && selectedOrganizations.length === 0) {
      // For superadmins with no org selected, don't fetch yet
      setLoading(false)
      return
    }

    fetchUsers()
  }, [selectedOrganizations])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      let response

      if (isSuperAdmin && selectedOrganizations.length > 0) {
        // If superadmin has selected organizations, fetch users from those orgs
        const orgIds = selectedOrganizations.map((org) => org._id).join(",")
        response = await getUsers({ organizationIds: orgIds })
      } else if (!isSuperAdmin) {
        // For non-superadmins, fetch users from their organization
        response = await getUsers()
      } else {
        // Superadmin with no orgs selected - return empty array
        setUsers([])
        setLoading(false)
        return
      }

      setUsers(
        response.data.map((user) => ({
          id: user._id,
          ...user,
        })),
      )
    } catch (error) {
      showNotification("Failed to fetch users", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async (e) => {
    const query = e.target.value
    setSearchQuery(query)

    if (query.length < 2) {
      fetchUsers()
      return
    }

    setSearching(true)
    try {
      let response

      if (isSuperAdmin && selectedOrganizations.length > 0) {
        const orgIds = selectedOrganizations.map((org) => org._id).join(",")
        response = await searchUsers(query, { organizationIds: orgIds })
      } else {
        response = await searchUsers(query)
      }

      setUsers(
        response.data.map((user) => ({
          id: user._id,
          ...user,
        })),
      )
    } catch (error) {
      console.error("Search error:", error)
    } finally {
      setSearching(false)
    }
  }

  const handleCreateUser = () => {
    navigate("/users/create")
  }

  const handleRowClick = (row) => {
    navigate(`/users/${row.id}`)
  }

  const handleOrganizationChange = (newValue) => {
    setSelectedOrganizations(newValue)
  }

  const setUserActive = async (row, isActive) => {
    try {
      await updateUser(row.id, { isActive })
      setUsers((prev) => prev.map((item) => (item.id === row.id ? { ...item, isActive } : item)))
      showNotification(isActive ? "User activated" : "User deactivated", "success")
    } catch (error) {
      showNotification(error.message || "Failed to update user", "error")
    } finally {
      setPendingAction(null)
    }
  }

  const handleToggleActive = (row) => {
    if (row.isActive === false) {
      setUserActive(row, true)
    } else {
      setPendingAction({ type: "deactivate", row })
    }
  }

  const handleConfirmDelete = async () => {
    const { row } = pendingAction
    try {
      await deleteUser(row.id)
      setUsers((prev) => prev.filter((item) => item.id !== row.id))
      showNotification("User deleted successfully", "success")
    } catch (error) {
      showNotification(error.message || "Failed to delete user", "error")
    } finally {
      setPendingAction(null)
    }
  }

  const columns = [
    { id: "firstName", label: "First Name", minWidth: 120 },
    { id: "lastName", label: "Last Name", minWidth: 120 },
    { id: "email", label: "Email", minWidth: 170 },
    {
      id: "role",
      label: "Role",
      minWidth: 100,
      format: (value) => (
        <Chip
          label={value}
          color={value === "superadmin" ? "error" : value === "admin" ? "warning" : "primary"}
          size="small"
        />
      ),
    },
    {
      id: "organizationId",
      label: "Organization",
      minWidth: 170,
      format: (value, row) => row.organizationName || value || "-",
    },
    {
      id: "isActive",
      label: "Status",
      minWidth: 100,
      format: (value) => (
        <Chip label={value ? "Active" : "Inactive"} color={value ? "success" : "default"} size="small" />
      ),
    },
    {
      id: "createdAt",
      label: "Created",
      minWidth: 120,
      format: (value) => new Date(value).toLocaleDateString(),
    },
    ...(canManageUsers
      ? [
          {
            id: "actions",
            label: "",
            minWidth: 60,
            align: "right",
            sortable: false,
            format: (_, row) =>
              row.id === user._id || (row.role === "superadmin" && !isSuperAdmin) ? null : (
                <UserRowActions
                  user={row}
                  onToggleActive={handleToggleActive}
                  onDelete={(target) => setPendingAction({ type: "delete", row: target })}
                />
              ),
          },
        ]
      : []),
  ]

  return (
    <Box>
      <PageHeader
        title="Users"
        subtitle="Manage your users"
        breadcrumbs={[{ text: "Dashboard", link: "/dashboard" }, { text: "Users" }]}
        action
        actionText="Add User"
        actionIcon={<Add />}
        onActionClick={handleCreateUser}
      />

      {isSuperAdmin && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filter Users by Organization
          </Typography>
          <OrganizationFilter
            value={selectedOrganizations}
            onChange={handleOrganizationChange}
            label="Select Organizations"
            placeholder="Select one or more organizations"
          />
        </Paper>
      )}

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search users..."
          value={searchQuery}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searching ? (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ) : null,
          }}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <EmptyState
          title={isSuperAdmin && selectedOrganizations.length === 0 ? "Select Organizations" : "No Users Found"}
          description={
            isSuperAdmin && selectedOrganizations.length === 0
              ? "Please select one or more organizations to view users"
              : "Get started by adding your first user."
          }
          actionText={isSuperAdmin && selectedOrganizations.length === 0 ? null : "Add User"}
          actionIcon={isSuperAdmin && selectedOrganizations.length === 0 ? null : <Add />}
          onActionClick={isSuperAdmin && selectedOrganizations.length === 0 ? null : handleCreateUser}
          icon={<People sx={{ fontSize: 60 }} />}
        />
      ) : (
        <DataTable columns={columns} data={users} title="Users" onRowClick={handleRowClick} />
      )}

      <ConfirmDialog
        open={pendingAction?.type === "deactivate"}
        title="Deactivate user"
        message={`${lastTargetName.current} will no longer be able to sign in or use the app until you activate them again.`}
        confirmText="Deactivate"
        confirmColor="warning"
        onConfirm={() => setUserActive(pendingAction.row, false)}
        onCancel={() => setPendingAction(null)}
      />
      <ConfirmDialog
        open={pendingAction?.type === "delete"}
        title="Delete user"
        message={`Are you sure you want to delete ${lastTargetName.current}? This action cannot be undone.`}
        confirmText="Delete"
        confirmColor="error"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingAction(null)}
      />
    </Box>
  )
}

export default Users

